import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
} from '@mui/material';
import { useApiGet } from '../../../utils/httpClientTs';
import { getIRSFormsList } from '../../../endpoints/irsForms';
import { IRSListParams, IRSFormListItem } from '../../../types/irsForms';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../../Components/ServerSideTable';
import { SortOrder } from '../../../types/table';

interface IRSFormsListProps {
  formType: 'lc' | 'sc' | 'rg';
}

const IRSFormsList: React.FC<IRSFormsListProps> = ({ formType }) => {
  const { year } = useParams<{ year: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<string>('local_chapter_name');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [search, setSearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [delaySearch, setDelaySearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [statusId, setStatusId] = useState<string>(
    searchParams.get('status_id') || ''
  );
  const [assignedToMe, setAssignedToMe] = useState<boolean>(
    searchParams.get('assigned_to_me') === 'true'
  );
  const currentUserId = localStorage.getItem('user_id');

  const yearNum = year ? parseInt(year, 10) : new Date().getFullYear();

  // Update search params when values change
  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);

  useEffect(() => {
    updateSearchParams('status_id', statusId);
  }, [statusId, updateSearchParams]);

  useEffect(() => {
    updateSearchParams('assigned_to_me', assignedToMe ? 'true' : '');
  }, [assignedToMe, updateSearchParams]);

  // Update default sort order based on form type
  useEffect(() => {
    if (formType === 'sc') {
      setOrderBy('spd_chapter_name');
    } else if (formType === 'rg') {
      setOrderBy('reciprocity_name');
    } else {
      setOrderBy('local_chapter_name');
    }
  }, [formType, setOrderBy]);

  const params: IRSListParams = {
    page: page + 1, // API uses 1-based indexing
    page_size: rowsPerPage,
    year: yearNum,
    search: search || undefined,
    order_by: orderBy || undefined,
    order: order || undefined,
    status_id: statusId || undefined,
    assigned_to_me: assignedToMe ? 'true' : undefined,
    form_type: formType,
  };

  const { data, isLoading } = useApiGet(
    [
      'irs-forms-list',
      formType,
      yearNum,
      rowsPerPage,
      page,
      orderBy,
      order,
      search,
      statusId,
      assignedToMe,
    ],
    () => getIRSFormsList(params),
    {
      enabled: true,
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
      setPage(0); // Reset to first page when search changes
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const handleClick = (form: IRSFormListItem) => {
    const formTypePath =
      formType === 'lc'
        ? 'local-chapters'
        : formType === 'sc'
          ? 'spd'
          : 'reciprocity';
    navigate(`/annual-reports/irs-forms/${year}/${formTypePath}/${form.id}`);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const getChapterName = (row: IRSFormListItem): string => {
    if (formType === 'sc' && row.spd_chapter_name) {
      return row.spd_chapter_name;
    }
    if (formType === 'rg' && row.reciprocity_name) {
      return row.reciprocity_name;
    }
    return row.local_chapter_name || '';
  };

  const columns: ServerSideTableColumn<IRSFormListItem>[] = [
    {
      id:
        formType === 'sc'
          ? 'spd_chapter_name'
          : formType === 'rg'
            ? 'reciprocity_name'
            : 'local_chapter_name',
      label:
        formType === 'sc'
          ? 'SPD Name'
          : formType === 'rg'
            ? 'Reciprocity Name'
            : 'Chapter Name',
      width: '40%',
      firstCell: true,
      render: (value, row) => {
        const displayName = getChapterName(row);
        return (
          <Typography variant="body2">
            {displayName}
            {row.assigned_to_id === currentUserId && (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  ml: 1,
                  px: 1,
                  py: 0.25,
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  borderRadius: 1,
                  fontWeight: 'bold',
                }}
              >
                Assigned to Me
              </Typography>
            )}
          </Typography>
        );
      },
    },
    {
      id: 'irs_type_name',
      label: 'Form Type',
      width: '20%',
    },
    {
      id: 'assigned_to_name',
      label: 'Assigned To',
      width: '20%',
    },
    {
      id: 'status_name',
      label: 'Status',
      width: '20%',
    },
  ];

  const getRowStyle = (row: IRSFormListItem) => {
    if (row.assigned_to_id === currentUserId) {
      return {
        backgroundColor: 'action.selected',
        borderLeft: '4px solid',
        borderLeftColor: 'primary.main',
      };
    }
    return {};
  };

  const getFormTypeLabel = () => {
    if (formType === 'sc') return 'SPD IRS Forms';
    if (formType === 'rg') return 'Reciprocity IRS Forms';
    return 'Local Chapter IRS Forms';
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={1}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate('/annual-reports/irs-forms')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography
            variant="h4"
            color={'primary'}
            sx={{ flex: 1, textAlign: 'center' }}
          >
            {getFormTypeLabel()} - {year}
          </Typography>
          <Box sx={{ width: 100 }} /> {/* Spacer for centering */}
        </Box>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} paddingBottom={3}>
          <TextField
            label="Search"
            id="search-field"
            size="small"
            value={delaySearch}
            fullWidth={true}
            onChange={(event) => setDelaySearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} paddingBottom={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={statusId}
              label="Status"
              onChange={(e: SelectChangeEvent<string>) => {
                setStatusId(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {/* Status options will be populated from API if needed */}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} paddingBottom={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={assignedToMe}
                onChange={(e) => {
                  setAssignedToMe(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Assigned to Me"
          />
        </Grid>
      </Grid>
      <Paper sx={{ p: 2, width: '100%' }}>
        <ServerSideTable
          data={data?.data || []}
          columns={columns}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.pagination?.total_count || 0}
          totalPages={data?.pagination?.total_pages || 0}
          hasNext={data?.pagination?.has_next || false}
          hasPrev={data?.pagination?.has_prev || false}
          order={order}
          orderBy={String(orderBy)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSort={handleSort}
          onRowClick={handleClick}
          emptyMessage="No IRS forms found."
          getRowStyle={getRowStyle}
        />
      </Paper>
    </Grid>
  );
};

export default IRSFormsList;
